/**
 * ATTEST-EVIDENCE-REQUIRED-1 (item 2) — what sits between a statement and its
 * evidence slot: which checklist row this sentence draws on, and, where the
 * policy allows it, the flag-and-attest tick.
 *
 * The tick is the whole liability argument in one control. For the two waivable
 * assertions the platform cannot see the CPA's working papers, so the honest
 * design is not to pretend it can: name the document that would satisfy the
 * engine, and if the CPA has it in their own file instead, capture that as a
 * declaration recorded against their name. The judgment stays with the licensed
 * signer, and the record shows the platform asked.
 *
 * It is offered only for an assertion the policy marks
 * `requires_attached_source: false`. Rendering it anywhere else would offer a
 * waiver the engine will refuse to honour, which is worse than no tick at all.
 */
import type { ChecklistRow } from '@/components/attest/documentChecklist'

/**
 * PLACEHOLDER — final wording pending Leonid's review. This sentence is a
 * professional declaration that goes into the artifact under the signer's name,
 * so it is drafted here to be replaced, not to be kept.
 */
export const ATTESTATION_LABEL =
  'I have not attached a document here. This statement is supported by records '
  + 'in my engagement file, and I am attesting to it in my professional capacity.'

export interface StatementEvidenceNoteProps {
  /** The checklist rows this statement's assertion draws on. May be empty. */
  rows: ChecklistRow[]
  /** Whether the policy allows an attestation in place of a file here. */
  attestationOffered: boolean
  statementIndex: number
  checked: boolean
  onChange: (checked: boolean) => void
}

export function StatementEvidenceNote({
  rows, attestationOffered, statementIndex: sIdx, checked, onChange,
}: StatementEvidenceNoteProps) {
  if (rows.length === 0) return null

  const satisfied = rows.some(r => r.status === 'satisfied')

  return (
    <div className="mt-3 rounded-md bg-muted/40 p-3" data-testid={`draws-on-${sIdx}`}>
      <p className="text-xs text-muted-foreground">
        Draws on: <span className="font-medium text-foreground">
          {rows.map(r => r.label).join(', ')}
        </span>. Attach it below.
      </p>

      {/* Offered only while nothing of the right kind is attached. Once a
          document is there the tick is moot, and leaving a "no document"
          declaration ticked next to a document would put a contradiction into
          the record. */}
      {attestationOffered && !satisfied && (
        <label
          className="mt-2 flex items-start gap-3 text-xs"
          htmlFor={`records-on-file-${sIdx}`}
        >
          <input
            id={`records-on-file-${sIdx}`}
            type="checkbox"
            data-testid={`records-on-file-${sIdx}`}
            className="mt-0.5 h-4 w-4"
            checked={checked}
            onChange={e => onChange(e.target.checked)}
          />
          <span>{ATTESTATION_LABEL}</span>
        </label>
      )}
    </div>
  )
}
